import { css } from '@eti/styles';
import { ExpandableContent } from '@eti/components';
import { CaretDown, X, Check } from '@phosphor-icons/react';
import { RichTextContent, Text, vars } from '@etg/wings';
import { useTranslation } from '@eti/providers';
import { stylesVariables } from '../../utils/utils';
import { BUNDLE_PRODUCT } from '../../utils/constants';
import { TRACKING_PROPERTIES, useProductSnowplowTracking } from '../../../custom-hooks/useProductSnowplowTracking';
import { screenReaderOnly } from '../../../common/utils/styles';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  firstColumnWidth,
  recommendedBackground,
  rowsHeight
} = stylesVariables;
const rowStyles = isExpanded => css`
  background-color: ${isExpanded && recommendedBackground};
  border: 1px solid ${vars.colors.divider};
  border-bottom: unset;
  display: flex;
  flex-flow: column wrap;
  transition: background-color 0.3s ease-in-out;

  &:first-child {
    border-start-start-radius: 4px;
  }
`;
const rowHeaderStyles = description => css`
  align-items: stretch;
  background: none;
  border: none;
  color: ${description ? `${vars.colors.links.default}` : `${vars.colors.text}`};
  cursor: ${description ? 'pointer' : 'default'};
  display: flex;
  padding: 0;
  text-align: start;
  width: 100%;
`;
const featureColumnStyles = css`
  align-items: flex-start;
  display: flex;
  gap: 8px;
  min-height: ${rowsHeight};
  padding: 16px 8px;
  width: ${firstColumnWidth};
`;
const optionColumnStyles = (isRecommended, isExpanded) => css`
  align-items: center;
  background-color: ${isRecommended ? recommendedBackground : 'transparent'};
  border-inline-start: ${isExpanded ? '1px solid transparent' : `1px solid ${vars.colors.divider}`};
  display: flex;
  flex: 1;
  justify-content: center;
  transition: border-inline-start 0.3s ease-in-out;
`;
const rowDescriptionStyles = css`
  padding: 32px;
  padding-top: 0;
`;
const iconStyles = (isExpanded, description) => css`
  color: ${vars.colors.links.default};
  flex-shrink: 0;
  margin-top: '2px';
  opacity: ${description ? '1' : '0'};
  transform: rotateX(${isExpanded ? '180deg' : '0deg'});
  transition: transform 0.2s ease-in-out;
`;
const SingleRow = _ref => {
  let {
    isExpanded,
    setExpandedRow,
    singleRowData
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    sendClickEvent
  } = useProductSnowplowTracking('Bundle TD');
  const {
    description,
    name,
    optionColumns,
    pieces,
    productTypeId,
    rowIndex,
    weight,
    weightUnit
  } = singleRowData;
  const includedTitles = optionColumns.filter(option => option.included === true).map(option => option.title).join(', ');
  const piecesAndWeight = weight && weightUnit && t('Cart.CabinBaggage.Included.PiecesAndWeight', pieces, weight, weightUnit);
  const handleToggle = () => {
    if (!isExpanded) {
      sendClickEvent({
        property: TRACKING_PROPERTIES.READ_MORE,
        value: singleRowData.name
      });
    }
    if (!description) {
      return;
    }
    if (isExpanded) {
      setExpandedRow(null);
    } else {
      setExpandedRow(rowIndex);
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    className: rowStyles(isExpanded),
    "data-offer-rate": productTypeId,
    "data-testid": `${BUNDLE_PRODUCT}-feature-row-${rowIndex}`,
    children: [/*#__PURE__*/_jsxs("button", {
      ...(description && {
        'aria-controls': `${BUNDLE_PRODUCT}-product-${rowIndex}`,
        'aria-expanded': isExpanded
      }),
      className: rowHeaderStyles(description),
      "data-testid": `${BUNDLE_PRODUCT}-row-header-${rowIndex}`,
      onClick: handleToggle,
      type: "button",
      children: [/*#__PURE__*/_jsxs("div", {
        className: featureColumnStyles,
        "data-testid": `${BUNDLE_PRODUCT}-feature-name-${rowIndex}`,
        children: [/*#__PURE__*/_jsx(CaretDown, {
          "aria-hidden": "true",
          className: iconStyles(isExpanded, description),
          "data-testid": `${BUNDLE_PRODUCT}-caret-${rowIndex}`,
          size: 20
        }), /*#__PURE__*/_jsx(Text, {
          id: `${BUNDLE_PRODUCT}-feature-name-${rowIndex}`,
          children: name
        }), /*#__PURE__*/_jsx("span", {
          className: screenReaderOnly,
          children: `included in: ${includedTitles}`
        })]
      }), optionColumns.map(_ref2 => {
        let {
          included,
          isRecommended,
          optionId
        } = _ref2;
        return /*#__PURE__*/_jsxs("div", {
          className: optionColumnStyles(isRecommended, isExpanded),
          children: [included && piecesAndWeight && /*#__PURE__*/_jsx("p", {
            children: piecesAndWeight
          }), included && !piecesAndWeight && /*#__PURE__*/_jsx(Check, {
            "aria-hidden": "true",
            color: vars.colors.signal.positive.foreground,
            "data-testid": `${BUNDLE_PRODUCT}-icon-check-option${optionId}-${rowIndex}`,
            size: 20
          }), !included && /*#__PURE__*/_jsx(X, {
            "aria-hidden": "true",
            "data-testid": `${BUNDLE_PRODUCT}-icon-x-option${optionId}-${rowIndex}`,
            size: 20
          })]
        }, optionId);
      })]
    }), /*#__PURE__*/_jsx(ExpandableContent, {
      isExpanded: isExpanded,
      children: /*#__PURE__*/_jsx(RichTextContent, {
        "aria-labelledby": `${BUNDLE_PRODUCT}-feature-name-${rowIndex}`,
        className: rowDescriptionStyles,
        "data-testid": `${BUNDLE_PRODUCT}-feature-description-${rowIndex}`,
        id: `${BUNDLE_PRODUCT}-product-${rowIndex}`,
        children: description
      })
    })]
  });
};
export default SingleRow;
//# sourceMappingURL=SingleRow.js.map